/**
 * RMA / Returns admin queue.
 *
 * Route: /projects/:projectId/returns
 *
 * State machine (enforced server-side):
 *   pending      → approved | rejected | cancelled
 *   approved     → item_received | cancelled | rejected
 *   item_received → refunded
 *   refunded / rejected / cancelled → terminal
 *
 * Per-project isolation: all list/detail/advance calls are scoped by
 * :projectId in the URL, never shared across tenants.
 */
import { useState } from 'react';
import { formatMoney as fmtMoney } from '@/lib/money';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { apiHelpers, type ReturnRequest } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  item_received: 'bg-indigo-100 text-indigo-800',
  refunded: 'bg-green-100 text-green-800',
  rejected: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const NEXT: Record<string, string[]> = {
  pending: ['approved', 'rejected', 'cancelled'],
  approved: ['item_received', 'cancelled', 'rejected'],
  rejected: [],
  item_received: ['refunded'],
  refunded: [],
  cancelled: [],
};

function formatMoney(amount: number | undefined, currency: string | undefined) {
  if (amount == null) return '-';
  // Delegate to the single money formatter (amounts are minor units).
  return fmtMoney(amount, currency, { alwaysDecimals: true });
}

export default function ReturnsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [selected, setSelected] = useState<ReturnRequest | null>(null);
  const [advanceTarget, setAdvanceTarget] = useState('');
  const [advanceNote, setAdvanceNote] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [internalNote, setInternalNote] = useState('');

  const listQuery = useQuery({
    queryKey: ['returns', projectId, statusFilter, orderSearch, emailSearch],
    queryFn: async () => {
      const params: any = { limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (orderSearch) params.orderNumber = orderSearch;
      if (emailSearch) params.email = emailSearch;
      const res = await apiHelpers.listReturns(projectId!, params);
      return (res.data.data || []) as ReturnRequest[];
    },
    enabled: Boolean(projectId),
  });

  const detailQuery = useQuery({
    queryKey: ['return', projectId, selected?._id],
    queryFn: async () => {
      const res = await apiHelpers.getReturn(projectId!, selected!._id);
      const row = res.data.data as ReturnRequest;
      setInternalNote(row.internalNote || '');
      return row;
    },
    enabled: Boolean(projectId) && Boolean(selected?._id),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['returns', projectId] });

  const advanceMutation = useMutation({
    mutationFn: () => {
      const payload: any = { status: advanceTarget };
      if (advanceNote) payload.note = advanceNote;
      if (advanceTarget === 'refunded') {
        const amt = Math.round(parseFloat(refundAmount || '0') * 100);
        if (!amt || amt <= 0) {
          throw new Error('Refund amount required');
        }
        payload.refundAmount = amt;
      }
      return apiHelpers.advanceReturn(projectId!, selected!._id, payload);
    },
    onSuccess: () => {
      toast.success('Status advanced');
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['return', projectId, selected?._id] });
      setAdvanceTarget('');
      setAdvanceNote('');
      setRefundAmount('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to advance');
    },
  });

  const noteMutation = useMutation({
    mutationFn: () =>
      apiHelpers.updateReturnNote(projectId!, selected!._id, {
        internalNote: internalNote || undefined,
      }),
    onSuccess: () => {
      toast.success('Internal note saved');
      queryClient.invalidateQueries({ queryKey: ['return', projectId, selected?._id] });
    },
    onError: () => toast.error('Failed to save note'),
  });

  const items = listQuery.data || [];
  const detail = detailQuery.data || selected;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Returns (RMA)</h1>
        <p className="text-muted-foreground">
          Triage customer return requests from order to refund.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        {(['pending', 'approved', 'item_received', 'refunded', 'rejected'] as const).map((s) => (
          <Card key={s}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium capitalize">
                {s.replace('_', ' ')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{items.filter((r) => r.status === s).length}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle>Queue</CardTitle>
              <CardDescription>Newest first</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="item_received">Item received</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Order #"
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                className="w-32"
              />
              <Input
                placeholder="Email"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                className="w-48"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-muted-foreground py-12 text-center">No returns match.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order #</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Refund</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow
                    key={r._id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => setSelected(r)}
                  >
                    <TableCell className="font-medium">{r.orderNumber}</TableCell>
                    <TableCell>{r.customerEmail}</TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate text-sm">
                      {r.reason || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLOR[r.status] || ''}>
                        {r.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatMoney(r.refundAmount, r.currency)}</TableCell>
                    <TableCell>{format(new Date(r.createdAt), 'MMM d, yyyy')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) {
            setSelected(null);
            setAdvanceTarget('');
            setAdvanceNote('');
            setRefundAmount('');
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>RMA · {selected?.orderNumber}</DialogTitle>
            <DialogDescription>
              {selected &&
                `${selected.customerEmail} · submitted ${format(
                  new Date(selected.createdAt),
                  'MMM d, yyyy'
                )}`}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge className={STATUS_COLOR[detail.status] || ''}>
                  {detail.status.replace('_', ' ')}
                </Badge>
                {detail.refundAmount != null && (
                  <span className="text-sm">
                    Refund: {formatMoney(detail.refundAmount, detail.currency)}
                  </span>
                )}
              </div>

              {detail.reason && (
                <div>
                  <Label className="text-muted-foreground text-xs">Customer reason</Label>
                  <div className="bg-muted/40 mt-1 whitespace-pre-wrap rounded-md border p-3 text-sm">
                    {detail.reason}
                  </div>
                </div>
              )}

              {detail.items && detail.items.length > 0 && (
                <div>
                  <Label className="text-muted-foreground text-xs">Items</Label>
                  <ul className="mt-1 space-y-1 text-sm">
                    {detail.items.map((it, i) => (
                      <li key={i} className="flex justify-between rounded border px-2 py-1">
                        <span>{it.name}</span>
                        <span className="text-muted-foreground">× {it.quantity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {detail.statusHistory && detail.statusHistory.length > 0 && (
                <div>
                  <Label className="text-muted-foreground text-xs">History</Label>
                  <div className="mt-1 space-y-2">
                    {detail.statusHistory.map((h, i) => (
                      <div key={i} className="rounded-md border p-2 text-sm">
                        <div className="flex items-center justify-between">
                          <Badge className={STATUS_COLOR[h.status] || ''}>
                            {h.status.replace('_', ' ')}
                          </Badge>
                          <span className="text-muted-foreground text-xs">
                            {format(new Date(h.changedAt), 'MMM d, yyyy h:mm a')} · {h.changedBy}
                          </span>
                        </div>
                        {h.note && <p className="text-muted-foreground mt-1">{h.note}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="rma-note">Internal note</Label>
                <Textarea
                  id="rma-note"
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  rows={3}
                  maxLength={5000}
                  className="mt-1"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => noteMutation.mutate()}
                  disabled={noteMutation.isPending}
                >
                  Save note
                </Button>
              </div>

              {NEXT[detail.status]?.length > 0 && (
                <div className="space-y-2 rounded-md border p-3">
                  <Label>Advance status</Label>
                  <div className="flex flex-wrap gap-2">
                    <Select value={advanceTarget} onValueChange={setAdvanceTarget}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Next status" />
                      </SelectTrigger>
                      <SelectContent>
                        {NEXT[detail.status].map((s) => (
                          <SelectItem key={s} value={s}>
                            {s.replace('_', ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {advanceTarget === 'refunded' && (
                      <Input
                        placeholder="Refund amount (major units)"
                        type="number"
                        step="0.01"
                        min="0"
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(e.target.value)}
                        className="w-52"
                      />
                    )}
                  </div>
                  <Textarea
                    value={advanceNote}
                    onChange={(e) => setAdvanceNote(e.target.value)}
                    placeholder="Note (optional)"
                    rows={2}
                    maxLength={2000}
                  />
                  <Button
                    onClick={() => advanceMutation.mutate()}
                    disabled={!advanceTarget || advanceMutation.isPending}
                  >
                    Advance
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
